import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Button,
  SelectPicker,
  Message,
  Input,
  InputNumber,
  Toggle,
  Stack
} from 'rsuite';
import { useDispatch } from 'react-redux';
import { updateSetting } from '@/services/setting.service';
import { fetchFeaturesLeave } from '@/services/feature.service';

interface EditLeaveTypeDrawerProps {
  open: boolean;
  onClose: () => void;
  toaster: any;
  leaveTypeData: any;
}

const EditLeaveTypeDrawer: React.FC<EditLeaveTypeDrawerProps> = ({
  open,
  onClose,
  toaster,
  leaveTypeData
}) => {
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState({
    name: 'Leave',
    type: '',
    NBDays: null,
    NBMonths: null,
    category: '',
    increment: false,
    Days: '',
    comment: '',
    window: null,
    typeBalance: 'LeaveBalance',
    dateControl: ''
    // attachement: false
  });
  const [formError, setFormError] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && leaveTypeData) {
      setFormValue({
        name: 'Leave',
        type: leaveTypeData.type || '',
        NBDays: leaveTypeData.NBDays || null,
        NBMonths: leaveTypeData.NBMonths || null,
        category: leaveTypeData.category || '',
        increment: leaveTypeData.Increment || false,
        Days: leaveTypeData.Days || '',
        comment: leaveTypeData.comment || '',
        window: leaveTypeData.window || null,
        typeBalance: leaveTypeData.typeBalance || 'LeaveBalance',
        dateControl: leaveTypeData.dateControl || ''
        // attachement: leaveTypeData.Attachement || false
      });
    }
  }, [open, leaveTypeData]);

  const categoryOptions = [
    { label: 'Exceptional', value: 'exceptional' },
    { label: 'Regular', value: 'regular' },
    { label: 'Paid', value: 'paid' },
    { label: 'Unpaid', value: 'unpaid' }
  ];
  const dateControlOptions = [
    { label: 'Before', value: 'before' },
    { label: 'After', value: 'after' }
  ];
  const daysOptions = [
    { label: 'Business Days', value: 'businessDay' },
    { label: 'Calendar Days', value: 'calendarDay' }
  ];

  const handleSubmit = async () => {
    // Validation
    const errors: any = {};
    if (!formValue.type) {
      errors.type = 'Leave type is required';
    }

    if (!formValue.category) {
      errors.category = 'Category is required';
    } if (formValue.NBDays !== null && (formValue.NBDays < 0 || formValue.NBDays > 31)) {
      errors.NBDays = 'Number of days must be between 0 and 31';
    }

    if (formValue.NBMonths !== null && (formValue.NBMonths < 0 || formValue.NBMonths > 12)) {
      errors.NBMonths = 'Number of months must be between 0 and 12';
    }

    if (formValue.NBDays === null && formValue.NBMonths === null) {
      errors.NBDays = 'Number of days or months is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormError(errors);
      return;
    }

    setSubmitting(true);

    try {
      // Prepare payload
      const payload = {
        ...formValue,
        Increment: formValue.increment,
        dateControl: formValue.dateControl || ''
        // Attachement: formValue.attachement
      };

      await updateSetting(leaveTypeData._id, payload);

      dispatch(fetchFeaturesLeave());

      toaster.push(
        <Message type="success" duration={3000}>
          Leave Type updated successfully!
        </Message>,
        { placement: 'topCenter' }
      );

      onClose();
    } catch (error) {
      toaster.push(
        <Message type="error" duration={3000}>
          Error updating leave type: {error.message}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>Edit Leave Type</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary" loading={submitting}>
            Save
          </Button>
          <Button onClick={onClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form
          fluid
          formValue={formValue}
          onChange={value => setFormValue(value)}
          formError={formError}
        >
          <Form.Group>
            <Form.ControlLabel>Leave Type</Form.ControlLabel>
            <Form.Control
              name="type"
              accepter={Input}
              placeholder="Enter leave type"
              block
              errorMessage={formError.type}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Category</Form.ControlLabel>
            <Form.Control
              name="category"
              accepter={SelectPicker}
              data={categoryOptions}
              searchable={false}
              placeholder="Select category"
              block
              errorMessage={formError.category}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Number of Days</Form.ControlLabel>
            <Form.Control
              name="NBDays"
              accepter={InputNumber}
              min={0}
              max={31}
              placeholder="Enter number of days (0-31)"
              errorMessage={formError.NBDays}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Number of Months</Form.ControlLabel>
            <Form.Control
              name="NBMonths"
              accepter={InputNumber}
              min={0}
              max={12}
              placeholder="Enter number of months (0-12)"
              errorMessage={formError.NBMonths}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Days Type</Form.ControlLabel>
            <Form.Control
              name="Days"
              accepter={SelectPicker}
              data={daysOptions}
              searchable={false}
              placeholder="Select days type"
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Comment</Form.ControlLabel>
            <Form.Control
              name="comment"
              accepter={Input}
              placeholder="Enter additional comments"
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Increment</Form.ControlLabel>
            <Form.Control
              name="increment"
              accepter={Toggle}
              checked={formValue.increment}
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
            <Form.HelpText>Toggle on if leave days can be accumulated</Form.HelpText>
          </Form.Group>

          {/* <Form.Group>
            <Form.ControlLabel>Attachement Required</Form.ControlLabel>
            <Form.Control
              name="attachement"
              accepter={Toggle}
              checked={formValue.attachement}
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
            <Form.HelpText>Toggle on if documentation is required</Form.HelpText>
          </Form.Group> */}
          <Form.Group>
            <Form.ControlLabel>Date Control</Form.ControlLabel>
            <Form.Control
              name="dateControl"
              accepter={SelectPicker}
              data={dateControlOptions}
              searchable={false}
              placeholder="Select date control (Optional)"
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>Window Period (Optional)</Form.ControlLabel>
            <Form.Control
              name="window"
              accepter={InputNumber}
              min={0}
              placeholder="Enter window period in days"
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default EditLeaveTypeDrawer;
